import { useState, useEffect, useCallback } from 'react';
import { getDisponibilidad, getIncidentes } from '../../services/analitica';
import { toNullableNumber, isValidNumber } from '../../utils/numbers';

/**
 * Rango: 1 año hacia atrás desde ayer.
 * Si corta un mes, se toma desde el día 1 de ese mes.
 */
const getDateRange = () => {
  const now = new Date();
  // Fin = ayer
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  // Inicio = 1 año antes de ayer, día 1 del mes
  const from = new Date(yesterday.getFullYear() - 1, yesterday.getMonth(), 1);
  const y1 = from.getFullYear();
  const m1 = String(from.getMonth() + 1).padStart(2, '0');
  const y2 = yesterday.getFullYear();
  const m2 = String(yesterday.getMonth() + 1).padStart(2, '0');
  const d2 = String(yesterday.getDate()).padStart(2, '0');
  return `${y1}-${m1}-01,${y2}-${m2}-${d2}`;
};

/**
 * Agrupa registros diarios por mes (YYYY-MM) y calcula promedios mensuales.
 * También cuenta códigos de incidencia únicos por mes.
 */
const groupByMonth = (rows, field, secondaryField) => {
  const monthMap = {};
  rows.forEach(r => {
    const month = r.dia.substring(0, 7); // "YYYY-MM"
    if (!monthMap[month]) monthMap[month] = { entries: [], codes: new Set() };
    monthMap[month].entries.push({
      dia: r.dia,
      value: toNullableNumber(r[field]),
      valueSecondary: secondaryField ? toNullableNumber(r[secondaryField]) : null,
    });
    // Contar códigos únicos de incidencias
    const raw = r.codigos != null ? String(r.codigos).trim() : '';
    if (raw) {
      raw.split(',').forEach(c => {
        const code = c.trim();
        if (code) monthMap[month].codes.add(code);
      });
    }
  });
  const sorted = Object.keys(monthMap).sort();
  return sorted.map(month => {
    const { entries, codes } = monthMap[month];
    const sortedEntries = entries.sort((a, b) => a.dia.localeCompare(b.dia));
    const values = sortedEntries.map(e => e.value).filter(isValidNumber);
    const valuesSecondary = sortedEntries.map(e => e.valueSecondary).filter(isValidNumber);
    return {
      month,
      avg: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null,
      avgSecondary: valuesSecondary.length
        ? valuesSecondary.reduce((a, b) => a + b, 0) / valuesSecondary.length
        : null,
      daily: sortedEntries,
      incidentCount: codes.size,
    };
  });
};

/**
 * Transforma datos diarios del API a formato de componentes.
 * Cada tipo_servicio genera un objeto:
 *   { name, currentMonth, average, data: [promedios mensuales] }
 */
const transformMetrics = (rawData, canales, metricType) => {
  if (!Array.isArray(rawData) || !rawData.length) {
    return { personas: [], empresas: [] };
  }

  const field = metricType === 'disponibilidad' ? 'disponibilidad' : 'tiempo';
  const secondaryField = metricType === 'disponibilidad' ? 'disponibilidad_programada' : null;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const currentMonthKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}`;

  const buildGroup = (canalId) => {
    const filtered = rawData.filter(r => Number(r.canal) === canalId);

    // Agrupar por tipo_servicio
    const tipoMap = {};
    filtered.forEach(r => {
      const tipo = r.tipo_servicio;
      if (!tipoMap[tipo]) tipoMap[tipo] = [];
      tipoMap[tipo].push(r);
    });

    return Object.entries(tipoMap).map(([tipo, rows]) => {
      const monthly = groupByMonth(rows, field, secondaryField);
      const currentMonthData = monthly.find(m => m.month === currentMonthKey);
      const allAvgs = monthly.map(m => m.avg).filter(isValidNumber);
      const allAvgsSecondary = monthly.map(m => m.avgSecondary).filter(isValidNumber);

      return {
        name: rows[0]?.nombre_servicio || rows[0]?.tipo_servicio_nombre || `Servicio ${tipo}`,
        canalId: canalId,
        tipoServicio: tipo,
        currentMonth: currentMonthData && isValidNumber(currentMonthData.avg)
          ? Number(currentMonthData.avg.toFixed(2))
          : null,
        currentMonthProgramado: currentMonthData && isValidNumber(currentMonthData.avgSecondary)
          ? Number(currentMonthData.avgSecondary.toFixed(2))
          : null,
        average: allAvgs.length
          ? Number((allAvgs.reduce((a, b) => a + b, 0) / allAvgs.length).toFixed(2))
          : null,
        averageProgramado: allAvgsSecondary.length
          ? Number((allAvgsSecondary.reduce((a, b) => a + b, 0) / allAvgsSecondary.length).toFixed(2))
          : null,
        data: monthly.map(m => (isValidNumber(m.avg) ? Number(m.avg.toFixed(2)) : null)),
        dataProgramado: monthly.map(m => (isValidNumber(m.avgSecondary) ? Number(m.avgSecondary.toFixed(2)) : null)),
        months: monthly.map(m => m.month),
        dailyData: monthly.map(m => m.daily),
        incidentCounts: monthly.map(m => m.incidentCount),
      };
    });
  };

  return {
    personas: buildGroup(canales.personas),
    empresas: buildGroup(canales.empresas),
  };
};

/**
 * Formatea segundos a string legible: "Xm Ys"
 */
const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m > 0 && sec > 0) return `${m}m ${sec}s`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
};

/**
 * Transforma incidentes del API al formato de IncidenciasTab.
 * API devuelve: [{ objetivo_id, objetivo_nombre, info_campo, fecha_inicial, fecha_final, duracion (seg) }]
 */
const transformIncidencias = (rawData) => {
  const empty = { summary: { count: 0, avgDuration: '0s', daysAffected: 0 }, details: [] };

  if (!Array.isArray(rawData) || !rawData.length) return empty;

  const details = rawData.map(r => ({
    id: r.objetivo_id || 0,
    objective: r.objetivo_nombre || '',
    incident: r.info_campo || '',
    start: r.fecha_inicial || '',
    end: r.fecha_final || '',
    duration: formatDuration(r.duracion),
    _duracionSeg: Number(r.duracion) || 0,
  }));

  const uniqueDays = new Set(details.map(d => d.start.substring(0, 10)).filter(Boolean));

  const totalSeg = details.reduce((s, d) => s + d._duracionSeg, 0);
  const avgSeg = details.length ? Math.round(totalSeg / details.length) : 0;

  return {
    summary: {
      count: details.length,
      avgDuration: formatDuration(avgSeg),
      daysAffected: uniqueDays.size,
    },
    details,
  };
};

export const useIndicadoresData = (channel, canalesConfig) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const fecha = getDateRange();
    const canales = canalesConfig[channel];

    try {
      const [rawDisp, rawInc] = await Promise.all([
        getDisponibilidad(fecha, { canal: `${canales.personas},${canales.empresas}` }),
        getIncidentes(fecha),
      ]);

      console.log('[Indicadores] Raw disponibilidad:', rawDisp);
      console.log('[Indicadores] Raw incidentes:', rawInc);

      setData({
        disponibilidad: transformMetrics(rawDisp, canales, 'disponibilidad'),
        tiempo: transformMetrics(rawDisp, canales, 'tiempo'),
        incidencias: transformIncidencias(rawInc),
      });
    } catch (err) {
      console.error('[Indicadores] Error fetching data:', err);
      setError(err.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [channel, canalesConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};
